'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface AlertFormProps {
  symbol?: string;
  onClose: () => void;
  onCreated?: () => void;
}

interface AlertFormData {
  name: string;
  symbol: string;
  alertType: string;
  conditionText: string;
  thresholdValue: string;
  channels: string[];
  priority: 'high' | 'medium' | 'low';
}

const ALERT_TYPES = [
  { value: 'price_above', label: 'Price Above', icon: 'ArrowTrendingUpIcon', description: 'Trigger when price exceeds target' },
  { value: 'price_below', label: 'Price Below', icon: 'ArrowTrendingDownIcon', description: 'Trigger when price drops below target' },
  { value: 'pct_change_up', label: '% Change Up', icon: 'ArrowUpIcon', description: 'Trigger on positive % move' },
  { value: 'pct_change_down', label: '% Change Down', icon: 'ArrowDownIcon', description: 'Trigger on negative % move' },
  { value: 'rsi_overbought', label: 'RSI Overbought', icon: 'SignalIcon', description: 'RSI exceeds threshold (default 70)' },
  { value: 'rsi_oversold', label: 'RSI Oversold', icon: 'SignalIcon', description: 'RSI drops below threshold (default 30)' },
  { value: 'macd_crossover', label: 'MACD Crossover', icon: 'ChartBarIcon', description: 'MACD line crosses signal line' },
  { value: 'volume_spike', label: 'Volume Spike', icon: 'BoltIcon', description: 'Volume exceeds average by threshold' },
  { value: 'custom', label: 'Custom Condition', icon: 'AdjustmentsHorizontalIcon', description: 'Write a custom alert condition' },
];

const CHANNELS = [
  { value: 'push', label: 'Push Notification', icon: 'BellIcon' },
  { value: 'email', label: 'Email', icon: 'EnvelopeIcon' },
  { value: 'sms', label: 'SMS', icon: 'ChatBubbleLeftIcon' },
  { value: 'webhook', label: 'Webhook', icon: 'LinkIcon' },
];

const PRIORITY_OPTIONS = [
  { value: 'high', label: 'High', color: 'text-red-400 border-red-500/40 bg-red-500/10' },
  { value: 'medium', label: 'Medium', color: 'text-amber-400 border-amber-500/40 bg-amber-500/10' },
  { value: 'low', label: 'Low', color: 'text-slate-400 border-slate-500/40 bg-slate-500/10' },
];

function buildConditionText(type: string, symbol: string, threshold: string): string {
  const sym = symbol || 'asset';
  const val = threshold || '0';
  switch (type) {
    case 'price_above': return `${sym} price rises above $${val}`;
    case 'price_below': return `${sym} price falls below $${val}`;
    case 'pct_change_up': return `${sym} gains more than ${val}% in a session`;
    case 'pct_change_down': return `${sym} drops more than ${val}% in a session`;
    case 'rsi_overbought': return `${sym} RSI(14) exceeds ${val || '70'}`;
    case 'rsi_oversold': return `${sym} RSI(14) falls below ${val || '30'}`;
    case 'macd_crossover': return `${sym} MACD line crosses above signal line`;
    case 'volume_spike': return `${sym} volume spikes ${val || '2'}x above average`;
    default: return '';
  }
}

export default function AlertCreationPanel({ symbol: initialSymbol, onClose, onCreated }: AlertFormProps) {
  const [form, setForm] = useState<AlertFormData>({
    name: '',
    symbol: initialSymbol || '',
    alertType: 'price_above',
    conditionText: '',
    thresholdValue: '',
    channels: ['push'],
    priority: 'medium',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Auto-build condition text when type/symbol/threshold changes
  useEffect(() => {
    if (form.alertType !== 'custom') {
      const text = buildConditionText(form.alertType, form.symbol, form.thresholdValue);
      setForm(p => ({ ...p, conditionText: text }));
    }
  }, [form.alertType, form.symbol, form.thresholdValue]);

  const toggleChannel = (ch: string) => {
    setForm(p => ({
      ...p,
      channels: p.channels.includes(ch)
        ? p.channels.filter(c => c !== ch)
        : [...p.channels, ch],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.conditionText.trim()) {
      setError('Alert name and condition are required');
      return;
    }
    if (form.channels.length === 0) {
      setError('Select at least one notification channel');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/alerts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          alertType: form.alertType,
          conditionText: form.conditionText.trim(),
          symbol: form.symbol.trim().toUpperCase() || null,
          thresholdValue: form.thresholdValue ? parseFloat(form.thresholdValue) : null,
          channels: form.channels,
          priority: form.priority,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to create alert');
      setSuccess(true);
      onCreated?.();
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const needsThreshold = ['price_above', 'price_below', 'pct_change_up', 'pct_change_down', 'rsi_overbought', 'rsi_oversold', 'volume_spike'].includes(form.alertType);
  const thresholdLabel: Record<string, string> = {
    price_above: 'Price Target ($)',
    price_below: 'Price Target ($)',
    pct_change_up: 'Change % Threshold',
    pct_change_down: 'Change % Threshold',
    rsi_overbought: 'RSI Level (default 70)',
    rsi_oversold: 'RSI Level (default 30)',
    volume_spike: 'Volume Multiplier (e.g. 2)',
  };

  if (success) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-12 h-12 bg-emerald-500/20 border border-emerald-500/40 rounded-full flex items-center justify-center mb-3">
          <Icon name="CheckIcon" size={24} variant="outline" className="text-emerald-400" />
        </div>
        <p className="text-sm font-mono font-semibold text-emerald-400">Alert Created!</p>
        <p className="text-xs text-slate-500 font-mono mt-1">{form.name}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Alert Name */}
      <div>
        <label className="text-xs text-slate-400 font-mono block mb-1.5">Alert Name *</label>
        <input
          type="text"
          value={form.name}
          onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
          placeholder="e.g. AAPL Price Target Alert"
          className="w-full px-3 py-2 bg-[#0F172A] border border-[#334155] rounded text-sm text-slate-200 font-mono focus:outline-none focus:border-sky-500 transition-all"
        />
      </div>

      {/* Symbol */}
      <div>
        <label className="text-xs text-slate-400 font-mono block mb-1.5">Symbol</label>
        <input
          type="text"
          value={form.symbol}
          onChange={e => setForm(p => ({ ...p, symbol: e.target.value.toUpperCase() }))}
          placeholder="AAPL, BTC, SPX..."
          className="w-full px-3 py-2 bg-[#0F172A] border border-[#334155] rounded text-sm text-slate-200 font-mono focus:outline-none focus:border-sky-500 transition-all"
        />
      </div>

      {/* Alert Type */}
      <div>
        <label className="text-xs text-slate-400 font-mono block mb-1.5">Alert Type *</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {ALERT_TYPES.map(at => (
            <button
              key={at.value}
              type="button"
              onClick={() => setForm(p => ({ ...p, alertType: at.value }))}
              className={`text-left px-3 py-2 rounded border transition-all ${
                form.alertType === at.value
                  ? 'bg-sky-500/10 border-sky-500/50 text-sky-400' :'bg-[#0F172A] border-[#334155] text-slate-400 hover:border-[#475569] hover:text-slate-200'
              }`}
            >
              <p className="text-xs font-mono font-semibold">{at.label}</p>
              <p className="text-[10px] text-slate-500 mt-0.5">{at.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Threshold Value */}
      {needsThreshold && (
        <div>
          <label className="text-xs text-slate-400 font-mono block mb-1.5">
            {thresholdLabel[form.alertType] || 'Threshold Value'}
          </label>
          <input
            type="number"
            value={form.thresholdValue}
            onChange={e => setForm(p => ({ ...p, thresholdValue: e.target.value }))}
            placeholder="Enter value..."
            step="any"
            className="w-full px-3 py-2 bg-[#0F172A] border border-[#334155] rounded text-sm text-slate-200 font-mono focus:outline-none focus:border-sky-500 transition-all"
          />
        </div>
      )}

      {/* Condition Text */}
      <div>
        <label className="text-xs text-slate-400 font-mono block mb-1.5">
          Condition Description {form.alertType === 'custom' ? '*' : '(auto-generated)'}
        </label>
        <textarea
          value={form.conditionText}
          onChange={e => setForm(p => ({ ...p, conditionText: e.target.value }))}
          placeholder="Describe the alert condition..."
          rows={2}
          readOnly={form.alertType !== 'custom'}
          className={`w-full px-3 py-2 bg-[#0F172A] border border-[#334155] rounded text-sm font-mono focus:outline-none focus:border-sky-500 transition-all resize-none ${
            form.alertType !== 'custom' ? 'text-slate-500 cursor-default' : 'text-slate-200'
          }`}
        />
      </div>

      {/* Notification Channels */}
      <div>
        <label className="text-xs text-slate-400 font-mono block mb-1.5">Notification Channels *</label>
        <div className="grid grid-cols-2 gap-2">
          {CHANNELS.map(ch => (
            <button
              key={ch.value}
              type="button"
              onClick={() => toggleChannel(ch.value)}
              className={`flex items-center gap-2 px-3 py-2 rounded border transition-all ${
                form.channels.includes(ch.value)
                  ? 'bg-sky-500/10 border-sky-500/50 text-sky-400' :'bg-[#0F172A] border-[#334155] text-slate-400 hover:border-[#475569]'
              }`}
            >
              <Icon name={ch.icon as any} size={14} variant="outline" />
              <span className="text-xs font-mono">{ch.label}</span>
              {form.channels.includes(ch.value) && (
                <Icon name="CheckIcon" size={12} variant="outline" className="ml-auto" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Priority */}
      <div>
        <label className="text-xs text-slate-400 font-mono block mb-1.5">Priority</label>
        <div className="flex gap-2">
          {PRIORITY_OPTIONS.map(p => (
            <button
              key={p.value}
              type="button"
              onClick={() => setForm(prev => ({ ...prev, priority: p.value as any }))}
              className={`flex-1 py-2 text-xs font-mono rounded border transition-all ${
                form.priority === p.value ? p.color : 'bg-[#0F172A] border-[#334155] text-slate-500 hover:border-[#475569]'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded">
          <p className="text-xs text-red-400 font-mono">{error}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 pt-1">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 py-2.5 border border-[#334155] text-slate-400 hover:text-slate-200 text-sm font-mono rounded transition-all"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex-1 py-2.5 bg-sky-500 hover:bg-sky-400 disabled:opacity-50 text-white text-sm font-semibold font-mono rounded transition-all flex items-center justify-center gap-2"
        >
          {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="BellIcon" size={14} variant="outline" />}
          {saving ? 'Creating...' : 'Create Alert'}
        </button>
      </div>
    </form>
  );
}
